import { useState } from 'react';
import {
  Button,
  Card,
  DataTable,
  Field,
  Notice,
  Resourceful,
  Select,
  StatusBadge,
  TextInput,
  type Column,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useResource } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import {
  createSuggestionRule,
  listSuggestionRules,
  updateSuggestionRule,
  type SuggestionKind,
  type SuggestionRuleDraft,
  type SuggestionRuleView,
} from './api';
import type { LoyaltyKey, LoyaltyTranslate } from './i18n';

/**
 * «Что предложить клиенту» (ТЗ М3.3): подсказки оператору трёх типов —
 * следующая услуга, апгрейд, подписка. Маркетолог задаёт их здесь, сервер
 * подбирает под клиента, планшет показывает на карточке у въезда.
 */
export interface SuggestionsTabProps {
  readonly t: LoyaltyTranslate;
  readonly canManage: boolean;
  readonly onError: (message: string) => void;
}

const KINDS: readonly SuggestionKind[] = ['next_service', 'upgrade', 'subscription'];

function codes(text: string): string[] {
  return text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function positive(text: string): number | undefined {
  const n = Number(text);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

interface FormState {
  kind: SuggestionKind;
  title: string;
  target: string;
  fromServices: string;
  minDays: string;
  minVisits: string;
  windowDays: string;
  segments: string;
  priority: string;
}

const EMPTY: FormState = {
  kind: 'next_service',
  title: '',
  target: '',
  fromServices: '',
  minDays: '',
  minVisits: '',
  windowDays: '',
  segments: '',
  priority: '100',
};

function toDraft(form: FormState): SuggestionRuleDraft {
  const minDays = positive(form.minDays);
  const minVisits = positive(form.minVisits);
  const windowDays = positive(form.windowDays);
  const segmentCodes = codes(form.segments);
  const fromServiceCodes = codes(form.fromServices);
  return {
    kind: form.kind,
    title: form.title.trim(),
    priority: Number(form.priority) || 100,
    ...(form.kind === 'subscription'
      ? { targetPlanCode: form.target.trim() }
      : { targetServiceCode: form.target.trim() }),
    condition: {
      ...(segmentCodes.length ? { segmentCodes } : {}),
      ...(minVisits ? { minVisits } : {}),
      ...(windowDays ? { windowDays } : {}),
      ...(form.kind === 'upgrade' && fromServiceCodes.length ? { fromServiceCodes } : {}),
      ...(form.kind === 'next_service' && minDays ? { minDaysSinceService: minDays } : {}),
    },
  };
}

function fromView(rule: SuggestionRuleView, status: 'active' | 'paused'): SuggestionRuleDraft {
  return {
    kind: rule.kind,
    title: rule.title,
    status,
    priority: rule.priority,
    siteIds: rule.siteIds,
    condition: rule.condition,
    ...(rule.kind === 'subscription'
      ? { targetPlanCode: rule.targetPlanCode }
      : { targetServiceCode: rule.targetServiceCode }),
  };
}

export function SuggestionsTab({ t, canManage, onError }: SuggestionsTabProps) {
  const toast = useToast();
  const rules = useResource(() => listSuggestionRules(), []);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const kindLabel = (kind: SuggestionKind): string => t(`suggest.kind.${kind}` as LoyaltyKey);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    try {
      await action();
      toast.ok(t('suggest.saved'));
      rules.reload();
    } catch (err) {
      const shown = errorText(err);
      onError(shown.message);
    } finally {
      setBusy(false);
    }
  }

  const columns: ReadonlyArray<Column<SuggestionRuleView>> = [
    { key: 'kind', header: t('suggest.kind'), render: (r) => kindLabel(r.kind) },
    { key: 'title', header: t('suggest.titleField'), render: (r) => r.title },
    {
      key: 'target',
      header: t('suggest.target'),
      render: (r) => r.targetServiceCode ?? r.targetPlanCode ?? '—',
    },
    {
      key: 'conditions',
      header: t('suggest.conditions'),
      render: (r) =>
        [
          r.condition.fromServiceCodes?.length ? `← ${r.condition.fromServiceCodes.join(', ')}` : null,
          r.condition.minDaysSinceService ? `≥ ${r.condition.minDaysSinceService} дн.` : null,
          r.condition.minVisits
            ? `≥ ${r.condition.minVisits} виз. / ${r.condition.windowDays ?? 30} дн.`
            : null,
          r.condition.segmentCodes?.length ? r.condition.segmentCodes.join(', ') : null,
        ]
          .filter(Boolean)
          .join(' · ') || '—',
    },
    { key: 'priority', header: t('suggest.priority'), render: (r) => String(r.priority) },
    {
      key: 'status',
      header: '',
      render: (r) => (
        <span className="flex items-center gap-2">
          <StatusBadge tone={r.status === 'active' ? 'ok' : 'neutral'}>
            {r.status === 'active' ? t('suggest.active') : t('suggest.paused')}
          </StatusBadge>
          {canManage ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  updateSuggestionRule(r.id, fromView(r, r.status === 'active' ? 'paused' : 'active')),
                )
              }
            >
              {r.status === 'active' ? t('suggest.pause') : t('suggest.resume')}
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <Card title={t('suggest.title')}>
      <p className="mb-4 text-sm text-ink-muted">{t('suggest.hint')}</p>

      <Resourceful resource={rules}>
        {(rows) => (
          <DataTable rows={rows} columns={columns} rowKey={(r) => r.id} empty={t('suggest.empty')} />
        )}
      </Resourceful>

      {canManage ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Field label={t('suggest.kind')}>
            <Select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as SuggestionKind })}
            >
              {KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kindLabel(kind)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('suggest.titleField')} hint={t('suggest.titleHint')}>
            <TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label={form.kind === 'subscription' ? t('suggest.targetPlan') : t('suggest.targetService')}>
            <TextInput value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} />
          </Field>
          {form.kind === 'upgrade' ? (
            <Field label={t('suggest.fromServices')}>
              <TextInput
                value={form.fromServices}
                onChange={(e) => setForm({ ...form, fromServices: e.target.value })}
              />
            </Field>
          ) : null}
          {form.kind === 'next_service' ? (
            <Field label={t('suggest.minDays')}>
              <TextInput
                inputMode="numeric"
                value={form.minDays}
                onChange={(e) => setForm({ ...form, minDays: e.target.value })}
              />
            </Field>
          ) : null}
          <Field label={t('suggest.minVisits')}>
            <TextInput
              inputMode="numeric"
              value={form.minVisits}
              onChange={(e) => setForm({ ...form, minVisits: e.target.value })}
            />
          </Field>
          <Field label={t('suggest.windowDays')}>
            <TextInput
              inputMode="numeric"
              value={form.windowDays}
              onChange={(e) => setForm({ ...form, windowDays: e.target.value })}
            />
          </Field>
          <Field label={t('suggest.segments')}>
            <TextInput value={form.segments} onChange={(e) => setForm({ ...form, segments: e.target.value })} />
          </Field>
          <Field label={t('suggest.priority')}>
            <TextInput
              inputMode="numeric"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            />
          </Field>
          <div className="flex items-end">
            <Button
              tone="primary"
              disabled={busy || !form.title.trim() || !form.target.trim()}
              onClick={() =>
                void run(async () => {
                  await createSuggestionRule(toDraft(form));
                  setForm(EMPTY);
                })
              }
            >
              {t('suggest.add')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Notice tone="info">{t('suggest.readonly')}</Notice>
        </div>
      )}
    </Card>
  );
}
